// frontend/src/app/components/projects/project-corpus/import-job-stream.component.ts
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { ImportJob, Page } from '../../../project-corpus/domain/types';
import { ImportJobCardComponent } from './import-job-card.component';

/**
 * Vertically stacked stream of import-job cards. Newest first, collapsible
 * header chevron, clickable cards that emit a job-filter selection.
 *
 * Inputs:
 *  - `jobs: Page<ImportJob> | null`
 *  - `activeJobId: string | null` — id of the currently-filtering job, if any
 *
 * Outputs:
 *  - `jobClick: string | null` — emits the clicked job's id, or `null` if the
 *    user clicks the already-active card (toggling the filter off).
 */
@Component({
  selector: 'app-import-job-stream',
  standalone: true,
  imports: [CommonModule, ImportJobCardComponent],
  templateUrl: './import-job-stream.component.html',
  styleUrls: ['./import-job-stream.component.scss'],
})
export class ImportJobStreamComponent {
  private readonly _jobs = signal<Page<ImportJob> | null>(null);
  private readonly _activeJobId = signal<string | null>(null);
  private readonly _collapsed = signal(false);

  @Input()
  set jobs(value: Page<ImportJob> | null) {
    this._jobs.set(value);
  }
  get jobs(): Page<ImportJob> | null {
    return this._jobs();
  }

  @Input()
  set activeJobId(value: string | null) {
    this._activeJobId.set(value);
  }
  get activeJobId(): string | null {
    return this._activeJobId();
  }

  /** Whether undo buttons are shown on completed jobs (project in SETUP/INGESTION). */
  @Input() canUndo = false;

  /** Current user ID, used to flag own import jobs with a "you" badge. */
  @Input() currentUserId = '';

  /** PI user ID — PI can undo any import; members undo only their own. */
  @Input() piUserId = '';

  @Output() jobClick = new EventEmitter<string | null>();
  @Output() jobDetail = new EventEmitter<string>();
  @Output() titleChange = new EventEmitter<{ jobId: string; title: string }>();
  @Output() undoImport = new EventEmitter<string>();

  readonly currentJobs = this._jobs.asReadonly();
  readonly collapsed = this._collapsed.asReadonly();

  /** Newest-first ordering, derived from `startedAt`. */
  readonly orderedItems = computed<readonly ImportJob[]>(() => {
    const page = this._jobs();
    if (!page) return [];
    return [...page.items].sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  });

  readonly isEmpty = computed(() => this.orderedItems().length === 0);
  /** Show skeleton on initial load (before first data arrives). */
  readonly showSkeleton = computed(() => this._jobs() === null);

  toggleCollapsed(): void {
    this._collapsed.update((c) => !c);
  }

  onCardClick(jobId: string): void {
    // Clicking the active card again clears the filter; otherwise switch to it.
    this.jobClick.emit(jobId === this._activeJobId() ? null : jobId);
  }

  isActive(jobId: string): boolean {
    return jobId === this._activeJobId();
  }

  onDetail(id: string): void {
    this.jobDetail.emit(id);
  }
}
